import React, { useEffect, useMemo, useState } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { useLoaderData } from "react-router-dom";
import { IsNullOrEmpty } from "../../../services/CustomService";
import {
    addValidatedListingAdvice,
    deleteValidatedListing,
    searchValidatedListings,
    updateValidatedListing,
    updateValidatedListingPicture
} from "../../../data/admin/validatedListings";
import { getToolCategories } from "../../../data/loca/tools";

export async function loader() {
    const [toolCategories, listings] = await Promise.all([
        getToolCategories(),
        searchValidatedListings('latest', '10')
    ]);
    return { toolCategories, listings };
}

export default function ValidatedListings() {
    const { toolCategories, listings } = useLoaderData();
    const [items, setItems] = useState(listings ?? []);
    const [filters, setFilters] = useState(null);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [searchType, setSearchType] = useState('publicId');
    const [searchValue, setSearchValue] = useState('');
    const [selectedListing, setSelectedListing] = useState(null);
    const [detailsVisible, setDetailsVisible] = useState(false);
    const [picturePreviewVisible, setPicturePreviewVisible] = useState(false);
    const [picturePreview, setPicturePreview] = useState({ src: '', alt: '' });
    const [adviceVisible, setAdviceVisible] = useState(false);
    const [adviceListing, setAdviceListing] = useState(null);
    const [adviceMessage, setAdviceMessage] = useState('');
    const [formValues, setFormValues] = useState({
        name: '',
        description: '',
        smallDescription: '',
        categoryId: null,
        secondaryCategoryIds: [],
        customUrl: ''
    });
    const maxPictureSize = 1_000_000;

    useEffect(() => {
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            name: { value: null, matchMode: FilterMatchMode.CONTAINS },
            ownerName: { value: null, matchMode: FilterMatchMode.CONTAINS },
            ownerEmail: { value: null, matchMode: FilterMatchMode.CONTAINS }
        });
    }, []);

    const searchOptions = useMemo(() => ([
        { label: 'Id interne', value: 'id' },
        { label: 'PublicId', value: 'publicId' },
        { label: 'CustomUrl', value: 'customUrl' },
        { label: 'Email utilisateur', value: 'email' }
    ]), []);

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const openPicturePreview = (rowData) => {
        if (IsNullOrEmpty(rowData?.base64Picture)) {
            return;
        }

        setPicturePreview({
            src: rowData.base64Picture,
            alt: rowData.name ?? 'Photo annonce'
        });
        setPicturePreviewVisible(true);
    };

    const nameBodyTemplate = (rowData) => (
        <div className="flex align-items-center gap-2">
            {(!IsNullOrEmpty(rowData.base64Picture)) ? (
                <img
                    className="smallPicture"
                    alt={rowData.name}
                    src={rowData.base64Picture || null}
                    onClick={() => openPicturePreview(rowData)}
                    style={{ cursor: 'pointer' }}
                />
            ) : (
                <div className="no-picture no-picture-small"></div>
            )}
            <span>{rowData.name}</span>
        </div>
    );

    const priceBodyTemplate = (rowData) => {
        return `${rowData.priceDayCents / 100}\u20ac`;
    };

    const createdBodyTemplate = (rowData) => {
        return new Date(rowData.createdAt).toLocaleDateString('fr-FR');
    };

    const ownerBodyTemplate = (rowData) => (
        <div>
            {rowData.ownerUserProfileId ? (
                <a href={`/user/${rowData.ownerUserProfileId}`}>{rowData.ownerName}</a>
            ) : (
                <div>{rowData.ownerName}</div>
            )}
            <div className="text-muted">{rowData.ownerEmail}</div>
        </div>
    );

    const actionBodyTemplate = (rowData) => (
        <div className="flex gap-2">
            <Button
                label="Donner un conseil"
                size="small"
                severity="warning"
                onClick={() => handleAdvice(rowData)}
            />
            <Button
                label="Modifier"
                size="small"
                severity="info"
                onClick={() => handleDetails(rowData)}
            />
            <Button
                label="Supprimer"
                size="small"
                severity="danger"
                onClick={() => handleDelete(rowData)}
            />
        </div>
    );

    const handleSearch = async () => {
        if (!searchValue.trim()) {
            window.alert("Merci d'indiquer une valeur de recherche.");
            return;
        }
        const results = await searchValidatedListings(searchType, searchValue.trim());
        setItems(results ?? []);
    };

    const handlePictureChange = (event, rowData) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        if (!file.type.startsWith('image/')) {
            window.alert('Le fichier doit être une image.');
            event.target.value = '';
            return;
        }

        if (file.size > maxPictureSize) {
            window.alert("L'image doit faire au maximum 1 Mo.");
            event.target.value = '';
            return;
        }

        const reader = new FileReader();
        reader.onloadend = async () => {
            const base64Picture = reader.result;
            const response = await updateValidatedListingPicture(rowData.publicId, base64Picture);
            if (response?.status === 200) {
                const updatedPicture = response.base64Picture ?? base64Picture;
                setItems((prev) => prev.map((item) => item.publicId === rowData.publicId
                    ? { ...item, base64Picture: updatedPicture }
                    : item));
            }
        };
        reader.readAsDataURL(file);
        event.target.value = '';
    };

    const handleDetails = (rowData) => {
        setSelectedListing(rowData);
        setFormValues({
            name: rowData.name ?? '',
            description: rowData.description ?? '',
            smallDescription: rowData.smallDescription ?? '',
            categoryId: rowData.categoryId ?? null,
            secondaryCategoryIds: rowData.secondaryCategoryIds ?? [],
            customUrl: rowData.customUrl ?? ''
        });
        setDetailsVisible(true);
    };

    const handleDelete = async (rowData) => {
        if (!window.confirm("Supprimer cette annonce ?")) {
            return;
        }
        await deleteValidatedListing(rowData.publicId);
        setItems((prev) => prev.filter((item) => item.publicId !== rowData.publicId));
        if (selectedListing?.publicId === rowData.publicId) {
            setDetailsVisible(false);
            setSelectedListing(null);
        }
    };

    const handleAdvice = (rowData) => {
        setAdviceListing(rowData);
        setAdviceMessage('');
        setAdviceVisible(true);
    };

    const handleSendAdvice = async () => {
        if (!adviceListing) {
            return;
        }

        const message = adviceMessage.trim();
        if (!message) {
            window.alert('Le conseil ne peut pas être vide.');
            return;
        }

        await addValidatedListingAdvice(adviceListing.publicId, message);
        setAdviceVisible(false);
        setAdviceListing(null);
        setAdviceMessage('');
        window.alert('Conseil envoyé à l\'utilisateur.');
    };

    const handleUpdateListing = async () => {
        if (!selectedListing) {
            return;
        }
        const response = await updateValidatedListing(selectedListing.publicId, {
            name: formValues.name,
            description: formValues.description,
            smallDescription: formValues.smallDescription,
            categoryId: formValues.categoryId,
            secondaryCategoryIds: (formValues.secondaryCategoryIds ?? []).filter((id) => id !== formValues.categoryId),
            customUrl: formValues.customUrl
        });
        const updatedListing = {
            ...selectedListing,
            publicId: response?.publicId ?? response?.PublicId ?? selectedListing.publicId,
            name: response?.name ?? response?.Name ?? formValues.name,
            description: response?.description ?? response?.Description ?? formValues.description,
            smallDescription: response?.smallDescription ?? response?.SmallDescription ?? formValues.smallDescription,
            category: response?.category ?? response?.Category ?? selectedListing.category,
            categoryId: response?.categoryId ?? response?.CategoryId ?? formValues.categoryId,
            secondaryCategoryIds: response?.secondaryCategoryIds ?? response?.SecondaryCategoryIds ?? formValues.secondaryCategoryIds,
            secondaryCategories: response?.secondaryCategories ?? response?.SecondaryCategories ?? selectedListing.secondaryCategories,
            customUrl: response?.customUrl ?? response?.CustomUrl ?? formValues.customUrl
        };
        setItems((prev) => prev.map((item) => (item.publicId === updatedListing.publicId
            ? {
                ...item,
                name: updatedListing.name,
                description: updatedListing.description,
                smallDescription: updatedListing.smallDescription,
                category: updatedListing.category,
                categoryId: updatedListing.categoryId,
                secondaryCategoryIds: updatedListing.secondaryCategoryIds,
                secondaryCategories: updatedListing.secondaryCategories,
                customUrl: updatedListing.customUrl
            }
            : item)));
        setSelectedListing(updatedListing);
        setFormValues({
            name: updatedListing.name ?? '',
            description: updatedListing.description ?? '',
            smallDescription: updatedListing.smallDescription ?? '',
            categoryId: updatedListing.categoryId ?? null,
            secondaryCategoryIds: updatedListing.secondaryCategoryIds ?? [],
            customUrl: updatedListing.customUrl ?? ''
        });
        setDetailsVisible(false);
        setSelectedListing(null);
    };

    const header = (
        <div className="flex justify-content-between">
            <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Rechercher dans les résultats..." />
            </span>
        </div>
    );

    return (
        <div>
            <h1>Gestion des annonces validées</h1>
            <div className="card flex flex-column gap-3">
                <div className="flex flex-column gap-2">
                    <label htmlFor="validated-search"><strong>Recherche</strong></label>
                    <div className="flex flex-column md:flex-row gap-2 align-items-start md:align-items-center">
                        <Dropdown
                            value={searchType}
                            options={searchOptions}
                            onChange={(event) => setSearchType(event.value)}
                            placeholder="Type de recherche"
                        />
                        <InputText
                            id="validated-search"
                            value={searchValue}
                            onChange={(event) => setSearchValue(event.target.value)}
                            placeholder="Id, PublicId, CustomUrl ou email"
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    handleSearch();
                                }
                            }}
                        />
                        <Button label="Rechercher" size="small" onClick={handleSearch} />
                    </div>
                </div>
                <DataTable value={items} paginator showGridlines rows={25} dataKey="publicId" filters={filters} globalFilterFields={['name', 'ownerName', 'ownerEmail']} header={header} emptyMessage="Aucune annonce trouvée">
                    <Column header="Annonce" body={nameBodyTemplate} />
                    <Column field="brand" header="Marque" />
                    <Column field="model" header="Mod&egrave;le" />
                    <Column header="Cat&eacute;gorie" body={(rowData) => [rowData.category, ...(rowData.secondaryCategories ?? [])].filter(Boolean).join(', ')} />
                    <Column header="Prix/j" body={priceBodyTemplate} />
                    <Column header="Propri&eacute;taire" body={ownerBodyTemplate} />
                    <Column header="Cr&eacute;&eacute;e le" body={createdBodyTemplate} />
                    <Column header="" body={actionBodyTemplate} />
                </DataTable>
            </div>
            <Dialog
                header="Détails de l'annonce"
                visible={detailsVisible}
                style={{ width: '50vw' }}
                baseZIndex={2000}
                appendTo={typeof window !== 'undefined' ? document.body : undefined}
                dismissableMask
                onHide={() => {
                    setDetailsVisible(false);
                    setSelectedListing(null);
                }}
                footer={(
                    <div className="flex justify-content-end gap-2">
                        <Button label="Enregistrer" size="small" onClick={handleUpdateListing} />
                        <Button
                            label="Fermer"
                            size="small"
                            severity="secondary"
                            onClick={() => {
                                setDetailsVisible(false);
                                setSelectedListing(null);
                            }}
                        />
                    </div>
                )}
            >
                {selectedListing ? (
                    <div className="flex flex-column gap-3">
                        <div className="flex flex-column gap-2">
                            <label htmlFor="listing-name"><strong>Nom</strong></label>
                            <InputText
                                id="listing-name"
                                value={formValues.name}
                                onChange={(event) => setFormValues((prev) => ({ ...prev, name: event.target.value }))}
                            />
                            <span className="text-muted">
                                {selectedListing.brand} {selectedListing.model}
                            </span>
                        </div>
                        <div className="flex flex-column gap-2">
                            <label htmlFor="listing-picture"><strong>Photo</strong></label>
                            <input
                                id="listing-picture"
                                type="file"
                                accept="image/*"
                                onChange={(event) => handlePictureChange(event, selectedListing)}
                            />
                        </div>
                        <div className="flex flex-column gap-2">
                            <label htmlFor="listing-category"><strong>Catégorie</strong></label>
                            <Dropdown
                                id="listing-category"
                                value={formValues.categoryId}
                                options={toolCategories}
                                optionLabel="name"
                                optionValue="value"
                                placeholder="Sélectionner une catégorie"
                                onChange={(event) => setFormValues((prev) => ({ ...prev, categoryId: event.value }))}
                            />
                        </div>
                        <div className="flex flex-column gap-2">
                            <label htmlFor="listing-secondary-categories"><strong>Catégories secondaires</strong></label>
                            <MultiSelect
                                id="listing-secondary-categories"
                                value={(formValues.secondaryCategoryIds ?? []).filter((id) => id !== formValues.categoryId)}
                                options={toolCategories.filter((category) => category.value !== formValues.categoryId)}
                                optionLabel="name"
                                optionValue="value"
                                display="chip"
                                placeholder="Aucune"
                                onChange={(event) => setFormValues((prev) => ({ ...prev, secondaryCategoryIds: event.value }))}
                            />
                        </div>
                        <div className="flex flex-column gap-2">
                            <label htmlFor="listing-custom-url"><strong>URL personnalisée</strong></label>
                            <InputText
                                id="listing-custom-url"
                                value={formValues.customUrl}
                                onChange={(event) => setFormValues((prev) => ({ ...prev, customUrl: event.target.value }))}
                            />
                        </div>
                        <div className="flex flex-column gap-2">
                            <label htmlFor="listing-description"><strong>Description</strong></label>
                            <InputTextarea
                                id="listing-description"
                                value={formValues.description}
                                rows={4}
                                onChange={(event) => setFormValues((prev) => ({ ...prev, description: event.target.value }))}
                            />
                        </div>
                        <div className="flex flex-column gap-2">
                            <label htmlFor="listing-small-description"><strong>Description courte</strong></label>
                            <InputTextarea
                                id="listing-small-description"
                                value={formValues.smallDescription}
                                rows={3}
                                onChange={(event) => setFormValues((prev) => ({ ...prev, smallDescription: event.target.value }))}
                            />
                        </div>
                    </div>
                ) : null}
            </Dialog>
            <Dialog
                header="Donner un conseil"
                visible={adviceVisible}
                style={{ width: '45vw' }}
                baseZIndex={2150}
                appendTo={typeof window !== 'undefined' ? document.body : undefined}
                dismissableMask
                onHide={() => {
                    setAdviceVisible(false);
                    setAdviceListing(null);
                    setAdviceMessage('');
                }}
                footer={(
                    <div className="flex justify-content-end gap-2">
                        <Button label="Envoyer" size="small" onClick={handleSendAdvice} />
                        <Button
                            label="Annuler"
                            size="small"
                            severity="secondary"
                            onClick={() => {
                                setAdviceVisible(false);
                                setAdviceListing(null);
                                setAdviceMessage('');
                            }}
                        />
                    </div>
                )}
            >
                {adviceListing ? (
                    <div className="flex flex-column gap-2">
                        <div><strong>Annonce :</strong> {adviceListing.name}</div>
                        <div><strong>Propriétaire :</strong> {adviceListing.ownerEmail}</div>
                        <label htmlFor="listing-advice"><strong>Conseil</strong></label>
                        <InputTextarea
                            id="listing-advice"
                            value={adviceMessage}
                            rows={5}
                            autoFocus
                            onChange={(event) => setAdviceMessage(event.target.value)}
                            placeholder="Exemple : merci de remplacer la première photo, elle est floue."
                        />
                    </div>
                ) : null}
            </Dialog>
            <Dialog
                header="Photo de l'annonce"
                visible={picturePreviewVisible}
                style={{ width: '60vw' }}
                baseZIndex={2100}
                appendTo={typeof window !== 'undefined' ? document.body : undefined}
                dismissableMask
                onHide={() => {
                    setPicturePreviewVisible(false);
                    setPicturePreview({ src: '', alt: '' });
                }}
            >
                {picturePreview.src ? (
                    <img
                        alt={picturePreview.alt}
                        src={picturePreview.src}
                        style={{ width: '100%', borderRadius: '8px' }}
                    />
                ) : (
                    <div className="no-picture no-picture-large"></div>
                )}
            </Dialog>
        </div>
    );
}
